import {
  $setSelection,
  createEditor,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalEditor,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical'
import * as React from 'react'
import { Suspense } from 'react'
import { createPortal } from 'react-dom'

const StickyComponent = React.lazy(() => import('./sticky-component'))

export type StickyColor = 'pink' | 'yellow'

export type SerializedStickyNode = Spread<
  {
    caption: any
    color: StickyColor
    xOffset: number
    yOffset: number
  },
  SerializedLexicalNode
>

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function safeJSONParse<T = any>(value: string): T | null {
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

/**
 * Create nested editor for caption.
 * (You can pass theme/nodes here if you want; keeping it simple.)
 */
function createCaptionEditor(): LexicalEditor {
  return createEditor()
}

export class StickyNode extends DecoratorNode<React.JSX.Element> {
  __x: number
  __y: number
  __color: StickyColor
  __caption: LexicalEditor

  static getType(): string {
    return 'sticky'
  }

  static clone(node: StickyNode): StickyNode {
    return new StickyNode(node.__x, node.__y, node.__color, node.__caption, node.__key)
  }

  constructor(
    x: number,
    y: number,
    color: StickyColor = 'yellow',
    caption?: LexicalEditor,
    key?: NodeKey
  ) {
    super(key)
    this.__x = x
    this.__y = y
    this.__color = color
    this.__caption = caption ?? createCaptionEditor()
  }

  // ---------------------------
  // JSON import/export (Lexical)
  // ---------------------------
  exportJSON(): SerializedStickyNode {
    return {
      caption: this.__caption.toJSON(),
      color: this.__color,
      type: 'sticky',
      version: 1,
      xOffset: this.__x,
      yOffset: this.__y,
    }
  }

  static importJSON(serializedNode: SerializedStickyNode): StickyNode {
    const node = new StickyNode(
      serializedNode.xOffset,
      serializedNode.yOffset,
      serializedNode.color
    )

    // Restore nested editor state if present
    const caption = serializedNode.caption
    if (caption?.editorState) {
      try {
        const nestedEditor = node.__caption
        const editorState = nestedEditor.parseEditorState(caption.editorState)
        if (!editorState.isEmpty()) {
          nestedEditor.setEditorState(editorState)
        }
      } catch {
        // ignore corrupt caption
      }
    }

    return node
  }

  // ---------------------------
  // DOM export/import (HTML)
  // ---------------------------

  /**
   * This is why you see "📝 Sticky note (yellow)" in raw HTML.
   * It's only a placeholder; the real sticky node comes back ONLY if importDOM runs.
   */
  exportDOM(): DOMExportOutput {
    const el = document.createElement('div')
    el.setAttribute('data-lexical-sticky', '1')
    el.setAttribute('data-sticky-x', String(this.__x))
    el.setAttribute('data-sticky-y', String(this.__y))
    el.setAttribute('data-sticky-color', this.__color)

    // Store caption as encoded JSON string
    try {
      const captionJson = this.__caption.toJSON()
      el.setAttribute(
        'data-sticky-caption',
        encodeURIComponent(JSON.stringify(captionJson))
      )
    } catch {
      // ignore
    }

    // Minimal placeholder for raw HTML viewers
    const placeholder = document.createElement('div')
    placeholder.className = 'sticky-placeholder'
    placeholder.textContent = `📝 Sticky note (${this.__color})`
    el.appendChild(placeholder)

    return { element: el }
  }

  /**
   *  Correct Lexical importDOM mapping:
   * - Return conversion ONLY for divs that match our sticky attributes.
   * - Do NOT return conversion for all divs.
   */
  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: Node) => {
        if (!(domNode instanceof HTMLElement)) return null
        if (domNode.getAttribute('data-lexical-sticky') !== '1') return null

        return {
          conversion: convertStickyElement,
          priority: 2,
        }
      },
    }
  }
  createDOM(_config: EditorConfig): HTMLElement {
    // "contents" so we don't introduce an extra wrapper in the editor DOM
    const div = document.createElement('div')
    div.style.display = 'contents'
    return div
  }

  updateDOM(): boolean {
    return false
  }

  // ---------------------------
  // Mutations
  // ---------------------------
  setPosition(x: number, y: number): void {
    const writable = this.getWritable()
    writable.__x = x
    writable.__y = y
    $setSelection(null)
  }

  toggleColor(): void {
    const writable = this.getWritable()
    writable.__color = writable.__color === 'pink' ? 'yellow' : 'pink'
  }

  // ---------------------------
  // React render
  // ---------------------------
  decorate(_editor: LexicalEditor, _config: EditorConfig): React.JSX.Element {
    return createPortal(
      <Suspense fallback={null}>
        <StickyComponent
          color={this.__color}
          x={this.__x}
          y={this.__y}
          nodeKey={this.getKey()}
          caption={this.__caption}
        />
      </Suspense>,
      document.body
    )
  }

  isIsolated(): boolean {
    return true
  }
}

/**
 * ✅ DOM conversion function used by importDOM()
 */
function convertStickyElement(domNode: HTMLElement): DOMConversionOutput | null {
  // Safety check (even though mapping already filtered)
  if (domNode.getAttribute('data-lexical-sticky') !== '1') return null

  const x = parseFloat(domNode.getAttribute('data-sticky-x') || '0')
  const y = parseFloat(domNode.getAttribute('data-sticky-y') || '0')
  const color = (domNode.getAttribute('data-sticky-color') as StickyColor) || 'yellow'

  const node = new StickyNode(x, y, color)

  const captionAttr = domNode.getAttribute('data-sticky-caption')
  if (captionAttr) {
    const decoded = safeDecodeURIComponent(captionAttr)
    const parsed = safeJSONParse<any>(decoded)

    if (parsed?.editorState) {
      try {
        const nestedEditor = node.__caption
        const editorState = nestedEditor.parseEditorState(parsed.editorState)
        if (!editorState.isEmpty()) {
          nestedEditor.setEditorState(editorState)
        }
      } catch {
        // ignore
      }
    }
  }

  return { node }
}

// ---------------------------
// Helpers
// ---------------------------
export function $isStickyNode(node: unknown): node is StickyNode {
  return node instanceof StickyNode
}

export function $createStickyNode(xOffset: number, yOffset: number): StickyNode {
  return new StickyNode(xOffset, yOffset, 'yellow')
}
